import type { ProductDto, SupplierDto } from '@pos/api-client';
import { UNITS, formatAmount, formatQuantity, formatQuantityValue, lineAmount, parseMoney, parseQuantity } from '@pos/business-logic';
import { BottomSheet, Button, EmptyState, IconButton, ErrorState, Field, Input, ListRow, Money, SearchField, Segmented, Skeleton, Textarea, cn, toast } from '@pos/ui';
import { ApiError } from '@pos/api-client';
import { Boxes, Check, ChevronRight, Minus, PackageMinus, PackagePlus, Plus, ScanLine, Trash2, Truck } from 'lucide-react';
import { useDeferredValue, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { BranchChip } from '../../app/BranchChip';
import { HeaderButton, Screen } from '../../app/Screen';
import { STOCK_PAGE_SIZE, lookupCode, useBranch, useProducts, useReceive, useStock, useStockSummary, useSuppliers } from '../../app/data';
import { ScannerSheet } from '../../lib/Scanner';


export function StockPage() {
  const [params, setParams] = useSearchParams();
  const low = params.get('low') === '1';
  const [q, setQ] = useState('');
  const query = useDeferredValue(q.trim());
  const [page, setPage] = useState(1);
  const stock = useStock(query, low, page);
  // Counted by the database over the whole catalogue, so the figure on a phone
  // is the shop's, not the page's.
  const summary = useStockSummary(query, low);
  const navigate = useNavigate();
  const [scan, setScan] = useState(false);
  const rows = stock.data?.items ?? [];
  const total = stock.data?.total ?? 0;
  const loaded = (page - 1) * STOCK_PAGE_SIZE + rows.length;
  useEffect(() => setPage(1), [query, low]);

  const onCode = async (code: string) => {
    setScan(false);
    const hit = await lookupCode(code);
    if (hit) navigate(`/products/${hit.product.id}`);
    else toast.error('Товар не найден', { description: `Штрихкод ${code}` });
  };

  return (
    <Screen
      title="Склад"
      actions={
        <>
          <HeaderButton label="Списание или корректировка" onClick={() => navigate('/stock/write-off')}>
            <PackageMinus />
          </HeaderButton>
          <HeaderButton label="Оформить приход" onClick={() => navigate('/stock/receive')}>
            <PackagePlus />
          </HeaderButton>
        </>
      }
    >
      <div className="space-y-3">
        <Segmented
          label="Показать"
          value={low ? 'low' : 'all'}
          onChange={(v) => setParams(v === 'low' ? { low: '1' } : {}, { replace: true })}
          options={[
            { value: 'all', label: 'Все товары' },
            { value: 'low', label: 'Заканчиваются' },
          ]}
          className="w-full [&>button]:flex-1"
        />
        <div className="flex items-center gap-2">
          <SearchField size="md" value={q} onChange={(e) => setQ(e.target.value)} onClear={() => setQ('')} placeholder="Название товара" aria-label="Поиск по складу" className="flex-1" />
          <IconButton label="Сканировать штрихкод" variant="secondary" onClick={() => setScan(true)}>
            <ScanLine className="size-5" />
          </IconButton>
        </div>
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm text-ink-3">
            {summary.data ? (
              <>
                Себестоимость остатка: <span className="font-medium text-ink tnum">{formatAmount(summary.data.stockValue)}</span>
              </>
            ) : null}
          </p>
          <BranchChip />
        </div>

        {stock.isError ? (
          <ErrorState description="Не удалось загрузить остатки." onRetry={() => void stock.refetch()} />
        ) : stock.isPending ? (
          <Skeleton className="h-80 rounded-lg" />
        ) : rows.length === 0 ? (
          <div className="rounded-2xl border border-line bg-surface">
            <EmptyState icon={<Boxes />} title={low ? 'Всего достаточно' : query ? 'Ничего не найдено' : 'Товаров нет'} description={low ? 'Нет товаров ниже минимального остатка.' : undefined} />
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-line bg-surface">
            {rows.map((r) => {
              const isLow = r.minStock > 0 && r.quantity <= r.minStock;
              return (
                <ListRow
                  key={`${r.productId}:${r.variantId ?? ''}`}
                  title={r.name}
                  subtitle={[r.categoryName, `${formatAmount(r.sellingPrice)} сум`].filter(Boolean).join(' · ')}
                  trailing={
                    <span className={cn('text-md font-semibold tnum', r.quantity <= 0 ? 'text-danger' : isLow ? 'text-warning' : 'text-ink')}>
                      {r.quantity <= 0 && r.quantity === 0 ? 'нет' : formatQuantity(r.quantity, r.unit)}
                    </span>
                  }
                  onClick={() => navigate(`/products/${r.productId}`)}
                />
              );
            })}
            {loaded < total && (
              <div className="border-t border-line p-3">
                <Button size="lg" className="w-full" loading={stock.isFetching} onClick={() => setPage((n) => n + 1)}>
                  Показать ещё · {total - loaded} осталось
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      <ScannerSheet open={scan} onOpenChange={setScan} onCode={(c) => void onCode(c)} />
    </Screen>
  );
}

// ───────────────────────── Receiving ─────────────────────────

interface Line {
  key: string;
  product: ProductDto;
  variantId: string | null;
  name: string;
  quantity: string;
  cost: string;
  /** New shelf price. Empty = leave the current selling price alone. */
  price: string;
  /** Selling price the product has right now, for comparison. */
  currentPrice: number;
  serials: string;
}

function lineQty(l: Line): number | null {
  const q = parseQuantity(l.quantity);
  if (q === null || q <= 0) return null;
  if (l.product.unit === 'PIECE' && q % 1000 !== 0) return null;
  return q;
}

function lineCost(l: Line): number | null {
  const c = parseMoney(l.cost);
  return c === null || c < 0 ? null : c;
}

/**
 * The new shelf price is optional: left empty, no `sellingPrice` goes to the API and the
 * price on the shelf stays as it is. `ok: false` means what was typed is not a price.
 */
function linePrice(l: Line): { ok: boolean; value: number | null } {
  if (!l.price.trim()) return { ok: true, value: null };
  const v = parseMoney(l.price);
  return v !== null && v > 0 ? { ok: true, value: v } : { ok: false, value: null };
}

function serialList(l: Line): string[] {
  return l.serials
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export function ReceivePage() {
  const navigate = useNavigate();
  const branchId = useBranch((s) => s.branchId);
  const suppliers = useSuppliers();
  const receive = useReceive();
  const [supplier, setSupplier] = useState<SupplierDto | null>(null);
  const [invoice, setInvoice] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [scan, setScan] = useState(false);
  const [pickSupplier, setPickSupplier] = useState(false);
  const [search, setSearch] = useState('');
  const query = useDeferredValue(search.trim());
  const found = useProducts(query, query.length >= 2);
  const [done, setDone] = useState<{ number: string; total: number } | null>(null);

  const add = (product: ProductDto, variantId: string | null, qty = 1000) => {
    setSearch('');
    const variant = product.variants.find((v) => v.id === variantId);
    const name = variant ? `${product.name} · ${variant.name}` : product.name;
    setLines((ls) => {
      const existing = ls.find((l) => l.product.id === product.id && l.variantId === variantId);
      if (existing) {
        const next = (lineQty(existing) ?? 0) + qty;
        return ls.map((l) => (l === existing ? { ...l, quantity: formatQuantityValue(next) } : l));
      }
      const cost = variant?.purchasePrice ?? product.purchasePrice;
      const currentPrice = variant?.sellingPrice ?? product.sellingPrice;
      return [
        { key: crypto.randomUUID(), product, variantId, name, quantity: formatQuantityValue(qty), cost: cost ? String(cost) : '', price: '', currentPrice, serials: '' },
        ...ls,
      ];
    });
    navigator.vibrate?.(20);
  };

  const onCode = async (code: string) => {
    setScan(false);
    const hit = await lookupCode(code);
    if (!hit) {
      toast.error('Товар не найден', { description: `Штрихкод ${code}. Создайте товар в разделе «Товары».` });
      return;
    }
    add(hit.product, hit.variantId, hit.quantity);
    toast.success(hit.product.name, { description: 'Добавлен в приход' });
  };

  const update = (key: string, patch: Partial<Line>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const step = (l: Line, d: number) => {
    const unit = l.product.unit === 'PIECE' ? 1000 : 100;
    update(l.key, { quantity: formatQuantityValue(Math.max(unit, (lineQty(l) ?? 0) + d * unit)) });
  };

  const invalid = lines.some((l) => lineQty(l) === null || lineCost(l) === null || !linePrice(l).ok);
  const total = lines.reduce((s, l) => s + lineAmount(lineCost(l) ?? 0, lineQty(l) ?? 0), 0);

  const submit = () => {
    receive.mutate(
      {
        branchId: branchId ?? undefined,
        supplierId: supplier?.id ?? null,
        invoiceNumber: invoice.trim() || null,
        note: 'Оформлено с телефона',
        post: true,
        items: lines.map((l) => ({
          productId: l.product.id,
          variantId: l.variantId,
          quantity: lineQty(l) ?? 0,
          purchasePrice: lineCost(l) ?? 0,
          sellingPrice: linePrice(l).value,
          serials: l.product.serialTracking !== 'NONE' ? serialList(l) : [],
        })),
      },
      {
        onSuccess: (r) => setDone({ number: r.number, total }),
        onError: (e) => toast.error('Приход не проведён', { description: e instanceof ApiError ? e.message : 'Проверьте связь и повторите' }),
      },
    );
  };

  if (done) {
    return (
      <Screen title="Приход" back="/stock">
        <div className="flex flex-col items-center px-4 pt-12 text-center">
          <div className="flex size-20 items-center justify-center rounded-full bg-success-soft text-success animate-pop-in">
            <Check className="size-10" strokeWidth={2.5} />
          </div>
          <h2 className="mt-6 text-2xl font-bold">Приход {done.number} проведён</h2>
          <p className="mt-2 text-md text-ink-3">
            На сумму <Money value={done.total} className="font-semibold text-ink" />. Остатки обновлены на всех кассах.
          </p>
          <div className="mt-10 flex w-full flex-col gap-2">
            <Button
              size="xl"
              variant="primary"
              onClick={() => {
                setDone(null);
                setLines([]);
                setInvoice('');
              }}
            >
              Новый приход
            </Button>
            <Button size="xl" onClick={() => navigate('/stock')}>
              К складу
            </Button>
          </div>
        </div>
      </Screen>
    );
  }

  return (
    <Screen title="Новый приход" back="/stock">
      <div className="space-y-4 pb-28">
        <div className="overflow-hidden rounded-2xl border border-line bg-surface">
          <ListRow
            leading={<Truck className="size-5 text-ink-3" />}
            title={supplier?.name ?? <span className="text-ink-3">Поставщик не выбран</span>}
            subtitle="Необязательно"
            trailing={<ChevronRight className="size-5 text-ink-4" />}
            onClick={() => setPickSupplier(true)}
          />
          <div className="px-4 py-3">
            <Input size="lg" placeholder="Номер накладной (необязательно)" aria-label="Номер накладной" value={invoice} onChange={(e) => setInvoice(e.target.value)} />
          </div>
        </div>

        <Button size="xl" variant="primary" className="w-full" icon={<ScanLine className="size-5" />} onClick={() => setScan(true)}>
          Сканировать товар
        </Button>
        <div className="relative">
          <SearchField size="lg" value={search} onChange={(e) => setSearch(e.target.value)} onClear={() => setSearch('')} placeholder="Или найдите по названию" aria-label="Найти товар" />
          {query.length >= 2 && (
            <div className="mt-2 overflow-hidden rounded-2xl border border-line bg-surface">
              {(found.data?.items ?? []).slice(0, 8).flatMap((p) =>
                (p.hasVariants && p.variants.length ? p.variants.map((v) => ({ p, v })) : [{ p, v: null }]).map(({ p: prod, v }) => (
                  <ListRow
                    key={`${prod.id}:${v?.id ?? ''}`}
                    title={v ? `${prod.name} · ${v.name}` : prod.name}
                    subtitle={`Остаток ${formatQuantity(v?.stock ?? prod.stock, prod.unit)}`}
                    trailing={<Plus className="size-5 text-primary" />}
                    onClick={() => add(prod, v?.id ?? null)}
                  />
                )),
              )}
              {found.data && found.data.items.length === 0 && <ListRow title={<span className="text-ink-3">Ничего не найдено</span>} />}
            </div>
          )}
        </div>

        {lines.length === 0 ? (
          <div className="rounded-lg border border-dashed border-line-strong bg-surface">
            <EmptyState icon={<PackagePlus />} title="Добавьте товары" description="Сканируйте штрихкоды на коробках или найдите товар по названию." />
          </div>
        ) : (
          <ul className="space-y-3">
            {lines.map((l) => {
              const q = lineQty(l);
              const c = lineCost(l);
              const price = linePrice(l);
              const serials = serialList(l);
              const unitLabel = UNITS[l.product.unit].label;
              return (
                <li key={l.key} className="rounded-2xl border border-line bg-surface p-4">
                  <div className="flex items-start gap-2">
                    <p className="min-w-0 flex-1 text-md font-medium">{l.name}</p>
                    <button type="button" aria-label={`Убрать ${l.name}`} onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))} className="-m-2 flex size-11 items-center justify-center rounded-full text-ink-3 active:bg-rail">
                      <Trash2 className="size-5" />
                    </button>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <Field label={`Количество, ${unitLabel}`} error={q === null ? 'Проверьте' : undefined}>
                      {(a) => (
                        <div className="flex items-center gap-1">
                          <button type="button" aria-label="Меньше" onClick={() => step(l, -1)} className="flex size-12 shrink-0 items-center justify-center rounded-md border border-line-strong active:bg-sunken">
                            <Minus className="size-5" />
                          </button>
                          <Input {...a} size="lg" inputMode="decimal" className="min-w-0 text-center tnum" value={l.quantity} onChange={(e) => update(l.key, { quantity: e.target.value })} />
                          <button type="button" aria-label="Больше" onClick={() => step(l, 1)} className="flex size-12 shrink-0 items-center justify-center rounded-md border border-line-strong active:bg-sunken">
                            <Plus className="size-5" />
                          </button>
                        </div>
                      )}
                    </Field>
                    <Field label={`Закупка за ${unitLabel}, сум`} error={c === null ? 'Укажите цену' : undefined}>
                      {(a) => <Input {...a} size="lg" inputMode="decimal" className="tnum" value={l.cost} onChange={(e) => update(l.key, { cost: e.target.value })} />}
                    </Field>
                  </div>
                  <div className="mt-3">
                    <Field
                      label={`Новая цена продажи за ${unitLabel}, сум`}
                      hint={
                        price.value !== null
                          ? `Было ${formatAmount(l.currentPrice)} — станет ${formatAmount(price.value)} на всех кассах`
                          : `Оставьте пустым — цена на полке останется ${formatAmount(l.currentPrice)}`
                      }
                      error={price.ok ? undefined : 'Проверьте цену'}
                    >
                      {(a) => (
                        <Input
                          {...a}
                          size="lg"
                          inputMode="decimal"
                          className="tnum"
                          placeholder="Не менять"
                          value={l.price}
                          onChange={(e) => update(l.key, { price: e.target.value })}
                        />
                      )}
                    </Field>
                  </div>
                  {l.product.serialTracking !== 'NONE' && (
                    <Field label="Серийные номера / IMEI" hint={`Через пробел или с новой строки · ${serials.length} из ${q ? q / 1000 : 0}`}>
                      {(a) => <Textarea {...a} rows={2} className="mt-1 font-mono text-sm" value={l.serials} onChange={(e) => update(l.key, { serials: e.target.value })} />}
                    </Field>
                  )}
                  <p className="mt-2 text-right text-sm text-ink-3">
                    Сумма: <span className="font-semibold text-ink tnum">{formatAmount(lineAmount(c ?? 0, q ?? 0))}</span>
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {lines.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] backdrop-blur-md">
          <div className="mx-auto flex max-w-lg items-center gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-ink-3">{lines.length} поз.</p>
              <Money value={total} className="text-xl font-bold" />
            </div>
            <Button size="xl" variant="primary" disabled={invalid} loading={receive.isPending} onClick={submit}>
              Провести
            </Button>
          </div>
        </div>
      )}

      <ScannerSheet open={scan} onOpenChange={setScan} onCode={(c) => void onCode(c)} title="Сканировать товар" />
      <BottomSheet open={pickSupplier} onOpenChange={setPickSupplier} title="Поставщик">
        <ul className="overflow-hidden rounded-lg border border-line">
          {[null, ...(suppliers.data ?? [])].map((s) => (
            <li key={s?.id ?? 'none'} className="border-b border-line last:border-b-0">
              <button
                type="button"
                className="flex min-h-14 w-full items-center gap-3 px-4 text-left text-md active:bg-sunken"
                onClick={() => {
                  setSupplier(s);
                  setPickSupplier(false);
                }}
              >
                <span className="flex-1">{s?.name ?? 'Без поставщика'}</span>
                {supplier?.id === s?.id && <Check className="size-5 text-primary" />}
              </button>
            </li>
          ))}
        </ul>
      </BottomSheet>
    </Screen>
  );
}
